// Figma 2174:13268 / 2174:13399, measured on the 393 × 852 reference frame.
// Keep screen geometry separate from the mirrored Fun Variables token tree.
export const DEPOSIT_METRICS = {
  reference: { width: 393, height: 852, insetTop: 59, insetBottom: 34 },
  narrowDeviceWidth: 380,
  sideMargin: 24,
  narrowSideMargin: 16,
  grabber: { width: 60, height: 5, radius: 5, top: 64 },
  header: {
    top: 92,
    height: 32,
    titleTop: 100,
    titleSize: 24,
    titleLineHeight: 23.76,
  },
  balance: {
    labelSize: 12,
    labelLineHeight: 13.8,
    labelTracking: -0.36,
    labelTop: 93,
    valueSize: 15,
    valueLineHeight: 17.25,
    valueTracking: -0.48,
    valueTop: 107,
    centsSize: 10.5,
    avatarSize: 32,
    avatarRight: 16,
    avatarGap: 6,
  },
  amount: {
    size: 96,
    tracking: -1.44,
    height: 114,
    centerY: 291,
    placeholderOpacity: 0.1,
  },
  method: { left: 36, top: 444, width: 56.6, height: 24 },
  notice: { bottom: 126 },
  fee: {
    size: 15,
    lineHeight: 15,
    tracking: -0.6,
    rightInset: 2,
    centerY: 459.5,
    buttonGap: 21,
  },
  // AmountRecommendationsRow, Figma 2814:7823 — four 82.25pt chips over 353.
  // Size comes from the `detail` type token, so only tracking is measured here.
  chips: {
    width: 329,
    height: 64,
    top: 483,
    paddingVertical: 5,
    chipWidth: 70,
    chipHeight: 48,
    gap: 8,
    radius: 14,
    tracking: -0.28,
  },
  button: { height: 56, top: 488, radius: 999, fontSize: 17, tracking: -0.68 },
  numpad: {
    width: 274,
    height: 198,
    top: 574,
    rowPitch: 60,
    lineHeight: 18,
    fontSize: 24,
    tracking: -0.36,
    decimalSize: 48,
    decimalTracking: -0.72,
    decimalOffsetY: -10,
    buttonGap: 30,
    bottomGap: 80,
  },
  disclosure: { keypadGap: 28, bottomGap: 24, height: 28 },
  watermarkTop: 818,
} as const

export function depositSideMargin(width: number): number {
  return width <= DEPOSIT_METRICS.narrowDeviceWidth
    ? DEPOSIT_METRICS.narrowSideMargin
    : DEPOSIT_METRICS.sideMargin
}

export const DEPOSIT_ACTIONS_HEIGHT =
  DEPOSIT_METRICS.button.top +
  DEPOSIT_METRICS.button.height -
  DEPOSIT_METRICS.method.top

/**
 * The notice is pinned above the action row and is taller than the row itself,
 * so the part standing proud of it has to be kept clear of the amount.
 */
export function depositNoticeOverhang(noticeHeight: number): number {
  return Math.max(
    0,
    DEPOSIT_METRICS.notice.bottom + noticeHeight - DEPOSIT_ACTIONS_HEIGHT,
  )
}

export function depositNumpadBottom(insetBottom: number): number {
  return (
    DEPOSIT_METRICS.numpad.bottomGap -
    DEPOSIT_METRICS.reference.insetBottom +
    insetBottom
  )
}

export function depositAmountCenter(
  height: number,
  insetTop: number,
  insetBottom: number,
  noticeOverhang = 0,
): number {
  const headerBottom =
    DEPOSIT_METRICS.header.top + DEPOSIT_METRICS.header.height
  const regionTop = headerBottom + insetTop - DEPOSIT_METRICS.reference.insetTop
  const regionBottom =
    height -
    depositNumpadBottom(insetBottom) -
    DEPOSIT_METRICS.numpad.height -
    DEPOSIT_METRICS.numpad.buttonGap -
    DEPOSIT_ACTIONS_HEIGHT
  const centerFraction =
    (DEPOSIT_METRICS.amount.centerY - headerBottom) /
    (DEPOSIT_METRICS.method.top - headerBottom)
  const firstCenter = regionTop + DEPOSIT_METRICS.amount.height / 2
  const lastCenter = Math.max(
    firstCenter,
    regionBottom - DEPOSIT_METRICS.amount.height / 2,
  )
  const centered = Math.min(
    lastCenter,
    Math.max(
      firstCenter,
      regionTop + (regionBottom - regionTop) * centerFraction,
    ),
  )
  // Only a notice tall enough to reach the amount moves it; one that clears it
  // leaves the design's own centring alone.
  const clearOfNotice =
    regionBottom - noticeOverhang - DEPOSIT_METRICS.amount.height / 2
  return Math.max(firstCenter, Math.min(centered, clearOfNotice))
}

/** Minimum canvas that keeps the amount, actions and keypad from overlapping. */
export function depositMinimumHeight(
  insetTop: number,
  insetBottom: number,
): number {
  return (
    DEPOSIT_METRICS.header.top +
    DEPOSIT_METRICS.header.height +
    insetTop -
    DEPOSIT_METRICS.reference.insetTop +
    DEPOSIT_METRICS.amount.height +
    DEPOSIT_ACTIONS_HEIGHT +
    DEPOSIT_METRICS.numpad.buttonGap +
    DEPOSIT_METRICS.numpad.height +
    depositNumpadBottom(insetBottom)
  )
}
